<template>
  <div
    v-if="isActive"
    data-component="booking-footer"
    class="booking-footer"
    :class="[
      { 'only-multiroom-switcher': !isOtherSteps && isMultiRoomActive },
      { '--modify-booking-mode': isBookingEditMode },
      { '--is-multi-room': itemsInCart.length > 1 },
      { '--step-2': step === 2 },
      { '--compactFooter': isOverviewPage },
      { '--loggedOutFooter': isEnhancementsLoggedOut },
    ]"
  >
    <div
      class="booking-footer-wrapper container"
      :class="[{ '--modify-booking-mode': isBookingEditMode }]"
    >
      <div
        v-if="isBookingEditMode || isOtherSteps"
        class="booking-footer-summary"
      >
        <div class="booking-footer-summary-wrapper">
          <div class="booking-footer-summary-total">
            {{ tc("booking.total") }}
          </div>
          <span>{{ getDisplayPrice({ price: cartTotal }) }}</span>
          <div class="booking-footer-summary-details">
            <a
              href="#"
              @click.prevent="$emit('show-cart')"
            >{{
              tc("booking.details")
            }}</a>
          </div>
        </div>
        <div
          v-if="isBookingEditMode && step === 0 && !isOverviewPage"
          class="booking-footer-special-code"
        >
          <button
            class="booking-footer-special-code-cta"
            :class="{
              'booking-footer-special-code-cta_selected': havePromoValues,
            }"
            @click.prevent="handleSpecialCodeClick($event)"
            @keydown.space.prevent="handleSpecialCodeClick($event)"
          >
            <template v-if="havePromoValues">
              <div class="booking-footer-special-code-cta-text">
                {{ promoValue }}
              </div>
              <div class="icon-close" />
            </template>
            <template v-else>
              <div class="icon-plus" />
              <div class="booking-footer-special-code-cta-text">
                {{ tc("booking.add") }} {{ tc("booking.specialCode") }}
              </div>
            </template>
          </button>
        </div>
      </div>
      <div
        v-if="isMultiRoomActive && !multiBookingCheckboxIsActive"
        class="booking-footer-rooms-switcher"
      >
        <button
          class="switch"
          :disabled="disableBackButton"
          @click="selectRoom(previousRoomId)"
          @keydown.space="selectRoom(previousRoomId)"
        >
          <em class="icon-chevron-left" />
        </button>
        <div class="room-info">
          <div class="title">
            {{
              tc("booking.selectMultipleRooms", {
                currentRoom: currentRoomId + 1,
                totalRooms: Number(query.roomQuantity),
              })
            }}
            <v-dropdown
              v-if="showTooltip"
              class="tooltip tooltip-variant--booking-footer"
              placement="top"
              :distance="22"
              :skidding="0"
            >
              <button
                class="tooltip-icon icon-info"
                role="presentation"
                :aria-label="tooltipLabel"
              />

              <template #popper>
                <div class="tooltip-variant--booking-footer tooltip-content">
                  <div class="tooltip-header">
                    <span
                      v-close-popper
                      :aria-label="tc('booking.close')"
                      tabindex="0"
                      class="icon-close"
                    />
                  </div>
                  <div class="tooltip-body">
                    {{ tooltipLabel }}
                  </div>
                </div>
              </template>
            </v-dropdown>
          </div>
          <div class="subtitle">
            {{ currentRoomGuestsSummary }}
          </div>
        </div>
        <button
          class="switch"
          :disabled="disableNextButton"
          @click="selectRoom(currentRoomId + 1)"
          @keydown.space="selectRoom(currentRoomId + 1)"
        >
          <em class="icon-chevron-right" />
        </button>
      </div>
      <div
        v-if="isBookingEditMode || isOtherSteps"
        class="booking-footer-skip"
      >
        <button
          v-if="isMultiRoomActive && stepZeroActive && !currentRoomIsLastRoom"
          :class="nextStepClass"
          :disabled="disableSkipButton"
          @click.prevent="selectRoom(currentRoomId + 1)"
          @keydown.space.prevent="selectRoom(currentRoomId + 1)"
        >
          {{
            tc("booking.skipToRoomSelection", {
              nextRoomId: currentRoomId + 2,
            })
          }}
        </button>
        <button
          v-else
          :class="nextStepClass"
          :disabled="disableSkipButton"
          @click.prevent="buttonAction"
          @keydown.space.prevent="buttonAction"
        >
          {{
            multiBookingCheckboxIsActive
              ? tc("booking.continueToOverview")
              : nextStepLabel
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import { capitalize } from '@utils/string-helpers'

  export default {
    name: 'BookingFooter',
    data: () => ({
      multiBookingCheckboxIsActive: false,
      showTooltip: false,
      tooltipLabel: '',
      disableBackButton: false,
      disableNextButton: false,
      nextRoomId: null,
      previousRoomId: null,
    }),
    computed: {
      ...mapState('booking', [
        'step',
        'nextStepLabel',
        'nextStepIndex',
        'nextStepClass',
        'query',
        'currentRoomId',
      ]),
      ...mapState('cart', [
        'enhancementsMultiBookingSelected',
        'cartPending',
        'validEnhancements',
      ]),
      ...mapGetters('booking', [
        'currentRoom',
        'getSummaryDetails',
        'currentRoomIsLastRoom',
      ]),
      ...mapGetters('cart', [
        'cartTotal',
        'enhancementsInRoomWithEmptySummary',
        'addOnsInRoomWithEmptyData',
        'itemsInCart',
      ]),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('currency', ['getDisplayPrice']),
      ...mapState('modifyReservation', [
        'isBookingEditMode',
        'modifyBookingPropData',
      ]),
      ...mapGetters('auth', ['isAuthenticated']),
      isMultiRoom() {
        return Number(this.query.roomQuantity) > 1
      },
      stepZeroActive() {
        return this.step === 0 && (this.isMultiRoom || this.isBookingEditMode)
      },
      isOtherSteps() {
        return (
          (this.step > 0
            && this.step < 3
            && !!this.nextStepIndex
            && !!this.nextStepLabel)
          || this.$route.name.includes('overview')
        )
      },
      isMultiRoomActive() {
        // TODO: check other possible scenarios and improve the validation
        return (
          this.isMultiRoom
          && [0, 1, 2].includes(this.step)
          && !this.$route.name.includes('overview')
        )
      },
      isActive() {
        return this.stepZeroActive || this.isOtherSteps
      },
      currentRoomGuests() {
        if (this.currentRoomId === 0) {
          return {
            adultGuestCount: this.query.adultGuestCount,
            childGuestCount: this.query.childGuestCount,
          }
        }
        return this.query.aditionalGuests[this.currentRoomId - 1]
      },
      currentRoomGuestsSummary() {
        return this.getSummaryDetails({
          customQuery: this.currentRoomGuests,
          capitalized: true,
        })
      },
      havePromoValues() {
        return (
          !!this.query.promotionCode
          || !!this.query.groupCode
          || !!this.query.iataNumber
        )
      },
      promoValue() {
        if (this.query.promotionCode) {
          return this.query.promotionCode
        } if (this.query.groupCode) {
          return this.query.groupCode
        } if (this.query.iataNumber) {
          return this.query.iataNumber
        }
      },
      disableSkipButton() {
        return (this.step === 2 && this.enhancementsInRoomWithEmptySummary) || this.addOnsInRoomWithEmptyData
      },
      isEnhancementsLoggedOut() {
        return !this.isAuthenticated && this.step !== 0
      },
      isOverviewPage() {
        return this.$route.name === 'rooms-overview' || this.$route.name === 'privileges-overview'
      },
    },
    watch: {
      isActive(val) {
        this.$emit('input', val)
      },
      enhancementsMultiBookingSelected(val) {
        this.multiBookingCheckboxIsActive = val
      },
      step() {
        this.updateTooltipVisibility()
      },
      async cartPending(val) {
        await this.updateTooltipLabel()
        await this.updateTooltipVisibility()
        this.updateControlButtons()
      },
      currentRoomId() {
        this.updateControlButtons()
      },
    },
    mounted() {
      this.multiBookingCheckboxIsActive = this.enhancementsMultiBookingSelected
      this.$emit('input', this.isActive)
    },
    methods: {
      ...mapActions('booking', [
        'goToStep',
        'selectRoom',
        'validateRoute',
        'updateQuery',
      ]),
      ...mapActions('cart', ['addEnhancementToAllRooms']),
      ...mapActions('rooms', ['getAvailableRooms']),
      async buttonAction() {
        if (this.multiBookingCheckboxIsActive) {
          await this.addEnhancementToAllRooms()
        }
        if (typeof this.nextStepIndex === 'number') {
          this.goToStep(this.nextStepIndex)
        } else if (typeof this.nextStepIndex === 'string') {
          if (this.nextStepIndex === 'validate') {
            this.validateRoute()
          } else {
            this.$router.push({
              name: this.nextStepIndex,
              query: {
                ...this.$route.query,
              },
            })
          }
        }
        this.resetSelectedRoom()
      },
      resetSelectedRoom() {
        this.selectRoom(0)
      },
      async removeEspecialCode() {
        await this.updateQuery({ prop: 'iataNumber', value: null })
        await this.updateQuery({ prop: 'promotionCode', value: null })
        await this.updateQuery({ prop: 'groupCode', value: null })
        await this.getAvailableRooms()
      },
      handleSpecialCodeClick(e) {
        this.havePromoValues
          ? this.removeEspecialCode()
          : this.$emit('edit', ['special-code', e.target])
      },
      updateTooltipVisibility() {
        this.showTooltip = this.step === 2
          && !!this.tooltipLabel
          && this.validEnhancements.some((item) => item.length === 0)
      },
      updateTooltipLabel() {
        let roomListLabel = ''
        const roomsWithoutEnhancements = []
        this.validEnhancements.forEach((enhancementList, index) => {
          if (enhancementList.length === 0) {
            roomsWithoutEnhancements.push(
              `${capitalize(this.tc('noums.roomSingular'))} ${index + 1}`
            )
          }
        })

        if (roomsWithoutEnhancements.length > 0) {
          roomListLabel += roomsWithoutEnhancements.join(', ')
        }
        this.tooltipLabel = this.tc(
          'booking.modifyReservationEnhancementUnavailable',
          {
            roomList: roomListLabel,
          }
        )
      },

      updateControlButtons() {
        const roomQuantity = Number(this.query.roomQuantity)
        const currentRoomIsFirst = this.currentRoomId === 0
        const currentRoomIsLast = this.currentRoomId === roomQuantity - 1

        this.disableNextButton = !this.currentRoom || currentRoomIsLast
        this.disableBackButton = currentRoomIsFirst
        this.previousRoomId = this.currentRoomId - 1
        this.nextRoomId = this.currentRoomId + 1
      },
    },
  }
</script>
